import { createServerFn } from '@tanstack/react-start'
import baseCatalog, { isPartnerOnlyCategory } from '#bv/data/products'
import { productImagePath } from '#bv/lib/productImageSlug'
import type { Category, Product } from '#bv/data/products'
import { getServiceSupabase } from '#bv/lib/supabaseAdminServer'
import { sanitizeMojibake } from '#bv/lib/sanitizeMojibake'
import {
  buildSharedAccessProductCopy,
  isSharedAccessListing,
  usesCustomSharedAccessCopy,
} from '#bv/lib/sharedAccountListing'

/** Normalize ALL merged products so storefront/API never emit UTF-8 mojibake. */
export function sanitizeMergedProduct(p: Product): Product {
  const next: Product = {
    ...p,
    name: sanitizeMojibake(p.name),
    shortDescription: sanitizeMojibake(p.shortDescription),
    description: sanitizeMojibake(p.description),
    deliveryInfo: sanitizeMojibake(p.deliveryInfo),
    features: p.features.map((f) => sanitizeMojibake(f)),
  }
  if (p.variants?.length) {
    next.variants = p.variants.map((v) => ({
      ...v,
      label: sanitizeMojibake(v.label),
      value: sanitizeMojibake(v.value),
    }))
  }
  if (p.faqs?.length) {
    next.faqs = p.faqs.map((f) => ({
      q: sanitizeMojibake(f.q),
      a: sanitizeMojibake(f.a),
    }))
  }
  if (p.bundlePicker) {
    next.bundlePicker = {
      ...p.bundlePicker,
      title: sanitizeMojibake(p.bundlePicker.title),
      description: p.bundlePicker.description ? sanitizeMojibake(p.bundlePicker.description) : undefined,
      tiers: p.bundlePicker.tiers.map((t) => ({
        ...t,
        label: sanitizeMojibake(t.label),
        blurb: t.blurb ? sanitizeMojibake(t.blurb) : undefined,
      })),
    }
  }
  return next
}

function isRetiredProductName(name: string): boolean {
  return name.trim().toLowerCase() === 'wondershare shared access (filmora)'
}

function isDeletedOverride(o: ProductListingOverrideRow): boolean {
  return o.hidden && (o.display_name?.trim().toLowerCase() ?? '') === '__deleted__'
}

export type ProductListingOverrideRow = {
  product_id: number
  display_name: string | null
  price_usd: string | number | null
  compare_at_usd: string | number | null
  delivery_info?: string | null
  hidden: boolean
}

export type ProductCustomRow = {
  synthetic_id: number
  name: string
  price: string | number
  compare_at_usd: string | number | null
  category: string
  image: string
  short_description: string
  hidden: boolean
  accountsplanet_uniqid?: string | null
}

function num(v: string | number | null | undefined): number | undefined {
  if (v == null) return undefined
  const n = typeof v === 'number' ? v : Number(v)
  return Number.isFinite(n) ? n : undefined
}

function isCategory(c: string): c is Category {
  return (
    c === 'discord' ||
    c === 'streaming' ||
    c === 'gaming' ||
    c === 'social' ||
    c === 'tiktok' ||
    c === 'instagram' ||
    c === 'youtube' ||
    c === 'software' ||
    c === 'zenith_lifetime' ||
    c === 'zenith_monthly' ||
    c === 'alchemy_lifetime' ||
    c === 'alchemy_token' ||
    c === 'alchemy_30_days'
  )
}

function customRowToProduct(row: ProductCustomRow): Product {
  const cat = isCategory(row.category) ? row.category : 'software'
  const price = num(row.price) ?? 0
  const cmp = num(row.compare_at_usd)
  const name = sanitizeMojibake(row.name.trim())
  const shortDesc = sanitizeMojibake(row.short_description.trim())
  const rowImage = row.image.trim()
  const imageFromRow = rowImage.startsWith('/') ? rowImage : `/placeholder.png`
  const image = imageFromRow === '/placeholder.png' ? productImagePath(name) : imageFromRow
  return {
    id: row.synthetic_id,
    name,
    image,
    description: `${name} digital delivery. After checkout, support shares fulfillment details.`,
    shortDescription: shortDesc || `${name} — $${price.toFixed(2)}`,
    price,
    ...(cmp != null && cmp > price ? { compareAtPrice: cmp } : {}),
    category: cat,
    stock: 999,
    rating: 4.65,
    reviewCount: 100 + (row.synthetic_id % 50),
    deliveryInfo: 'Manual delivery after payment verification',
    features: ['Fast support response', 'Status tracking', 'Replacement support window'],
    hideFromShop: row.hidden,
  }
}

function applyOverride(p: Product, o: ProductListingOverrideRow): Product {
  if (isDeletedOverride(o)) {
    return {
      ...p,
      hideFromShop: true,
    }
  }
  const price = num(o.price_usd)
  const cmp = num(o.compare_at_usd)
  const name = sanitizeMojibake(o.display_name?.trim() || p.name)
  const effectivePrice = price ?? p.price
  const next: Product = {
    ...p,
    name,
    hideFromShop: o.hidden ? true : (p.hideFromShop ?? false),
    ...(price != null ? { price } : {}),
  }
  if (cmp != null && cmp > effectivePrice) {
    next.compareAtPrice = cmp
  } else {
    delete next.compareAtPrice
  }
  const deliveryOverride = o.delivery_info?.trim()
  if (deliveryOverride) {
    next.deliveryInfo = sanitizeMojibake(deliveryOverride)
  }
  if (price != null || o.display_name) {
    if (isSharedAccessListing(name) && !usesCustomSharedAccessCopy(name)) {
      const shared = buildSharedAccessProductCopy(name, effectivePrice)
      next.shortDescription = shared.shortDescription
      if (!deliveryOverride) next.deliveryInfo = shared.deliveryInfo
      if (price != null) {
        next.description = shared.description
        next.features = shared.features
        next.faqs = shared.faqs
      }
    } else {
      next.shortDescription = `${name} — $${effectivePrice.toFixed(2)}`
    }
  }
  return next
}

export type ProductSmmPackagesRow = {
  product_id: number
  packages: unknown
  enabled: boolean
}

function applySmmPackageOverrides(products: Product[], smmRows: ProductSmmPackagesRow[]): Product[] {
  const byId = new Map(smmRows.map((r) => [r.product_id, r]))
  return products.map((p) => {
    if (!p.smmConfig) return p
    const row = byId.get(p.id)
    if (!row) return p
    if (row.enabled === false) return { ...p, hideFromShop: true }
    if (!Array.isArray(row.packages) || row.packages.length === 0) return p
    const packages = row.packages
      .map((pkg) => {
        const r = pkg as { label?: string; quantity?: number; price?: number }
        if (!r.label || !Number.isFinite(r.quantity) || !Number.isFinite(r.price)) return null
        return { label: String(r.label), quantity: Number(r.quantity), price: Number(r.price) }
      })
      .filter((x): x is { label: string; quantity: number; price: number } => x !== null)
    if (!packages.length) return p
    const minPrice = Math.min(...packages.map((x) => x.price))
    const nextCfg = p.smmConfig.tiers?.length
      ? {
          ...p.smmConfig,
          tiers: p.smmConfig.tiers.map((t) => (t.id === 'elite' ? { ...t, packages } : t)),
          packages,
        }
      : { ...p.smmConfig, packages }
    return {
      ...p,
      price: minPrice,
      shortDescription: `${p.name} — Elite, Standard & Budget · from $${minPrice.toFixed(2)}`,
      smmConfig: nextCfg,
    }
  })
}

/** Hub cards keep tier prices from build time; refresh from merged child SKUs after overrides. */
export function syncFamilyHubPricing(catalog: Product[]): Product[] {
  const byId = new Map(catalog.map((p) => [p.id, p]))
  return catalog.map((p) => {
    if (!p.bundlePicker?.tiers.length) return p
    const tiers = p.bundlePicker.tiers.map((tier) => {
      const child = byId.get(tier.productId)
      if (!child) return tier
      return { ...tier, price: child.price }
    })
    const prices = tiers.map((t) => t.price).filter((n) => Number.isFinite(n))
    if (!prices.length) return p
    const minPrice = Math.min(...prices)
    return {
      ...p,
      price: minPrice,
      bundlePicker: { ...p.bundlePicker, tiers },
    }
  })
}

/** Lowest live tier price for a bundle hub (uses catalog child SKUs when provided). */
export function bundleFromPriceUsd(product: Product, catalog?: Product[]): number | undefined {
  if (!product.bundlePicker?.tiers.length) return undefined
  const byId = catalog ? new Map(catalog.map((p) => [p.id, p])) : null
  const prices = product.bundlePicker.tiers.map((tier) => {
    const live = byId?.get(tier.productId)
    return live?.price ?? tier.price
  })
  return prices.length ? Math.min(...prices) : undefined
}

export function mergeCatalogSync(
  base: Product[],
  overrides: ProductListingOverrideRow[],
  custom: ProductCustomRow[],
  smmPackages: ProductSmmPackagesRow[] = [],
): Product[] {
  const byId = new Map(overrides.map((o) => [o.product_id, o]))
  const mergedBase = base.map((p) => {
    const o = byId.get(p.id)
    return o ? applyOverride(p, o) : { ...p }
  })
  const customProducts = custom.map(customRowToProduct)
  const merged = applySmmPackageOverrides(
    [...mergedBase, ...customProducts].filter((p) => !isRetiredProductName(p.name)).map(sanitizeMergedProduct),
    smmPackages,
  )
  return syncFamilyHubPricing(merged)
}

async function fetchOverridesAndCustom(): Promise<{
  overrides: ProductListingOverrideRow[]
  custom: ProductCustomRow[]
  smmPackages: ProductSmmPackagesRow[]
}> {
  const sb = getServiceSupabase()
  if (!sb) return { overrides: [], custom: [], smmPackages: [] }
  const [ovRes, cuRes, smmRes] = await Promise.all([
    sb.from('product_listing_overrides').select('*'),
    sb.from('product_custom').select('*').order('synthetic_id', { ascending: true }),
    sb.from('product_smm_packages').select('product_id, packages, enabled'),
  ])
  if (ovRes.error) console.warn('[catalog] overrides', ovRes.error.message)
  if (cuRes.error) console.warn('[catalog] custom', cuRes.error.message)
  if (smmRes.error) console.warn('[catalog] smm packages', smmRes.error.message)
  return {
    overrides: (ovRes.data as ProductListingOverrideRow[] | null) ?? [],
    custom: (cuRes.data as ProductCustomRow[] | null) ?? [],
    smmPackages: (smmRes.data as ProductSmmPackagesRow[] | null) ?? [],
  }
}

/** Merged catalog for storefront + checkout (server-side). */
export async function getMergedCatalog(): Promise<Product[]> {
  const base = baseCatalog.map((p) => ({ ...p }))
  const { overrides, custom, smmPackages } = await fetchOverridesAndCustom()
  return mergeCatalogSync(base, overrides, custom, smmPackages)
}

export const getMergedCatalogFn = createServerFn({ method: 'POST' }).handler(async (): Promise<Product[]> => {
  return getMergedCatalog()
})

function isLegacyRobuxListing(name: string, productId: number): boolean {
  if (productId === 130) return false
  const n = name.trim().toLowerCase()
  return (
    /robux\s*[—–-]\s*1[,.]?000/.test(n) ||
    /\b1\s*k\b.*\brobux\b/.test(n) ||
    /\brobux\b.*\b1,?000\b/.test(n) ||
    n.includes('1k robux') ||
    n === 'robux — 1,000'
  )
}

function isLegacyServerBoostListing(name: string, productId: number): boolean {
  if (productId === 134 || productId === 135) return false
  const n = name.trim().toLowerCase()
  return /discord\s+server\s+boost/i.test(n) && !/boosts$/i.test(name.trim())
}

export function productsShownMerged(list: Product[]): Product[] {
  return list.filter(
    (p) =>
      !p.hideFromShop &&
      !isPartnerOnlyCategory(p.category) &&
      !isLegacyRobuxListing(p.name, p.id) &&
      !isLegacyServerBoostListing(p.name, p.id),
  )
}

/** Single product for PDP / checkout; returns null if missing. (`hideFromShop` only affects browse grids.) */
export async function getMergedProductById(productId: number): Promise<Product | null> {
  const list = await getMergedCatalog()
  const p = list.find((x) => x.id === productId)
  if (!p || isPartnerOnlyCategory(p.category)) return null
  return p
}
